<template>
    <div>
        <!-- Floating Button -->
        <div class="chat-button" @click="toggleChat">
            <img src="/storage/images/chatbot.png" alt="The chatbot" width="40">
        </div>

        <!-- Chat Window -->
        <transition name="slide-up">
            <div v-if="showChat" class="chat-window shadow">
                <!-- Header -->
                <div class="chat-header">
                    <span>Chat Support</span>
                    <button class="close-btn" @click="toggleChat">✖</button>
                </div>

                <!-- Body -->
                <div class="chat-body" ref="chatBody">
                    <div v-for="(msg, index) in messages" :key="index" :class="['message', msg.sender]"
                        @click="handleOptionClick(msg)">
                        <a v-if="msg.sender === 'bot-option' && msg.url" :href="resolveOptionUrl(msg.url)"
                            :target="msg.openInNewTab ? '_blank' : '_self'"
                            rel="noopener noreferrer" @click.stop>
                            {{ msg.text }}
                        </a>
                        <span v-else>{{ msg.text }}</span>
                    </div>
                </div>

                <!-- Input -->
                <div class="chat-input">
                    <input v-model="userMessage" @keyup.enter="sendMessage" placeholder="Type your message..." />
                    <button @click="sendMessage">Send</button>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import axios from 'axios';

export default {
    name: 'ChatWidget',
    data() {
        return {
            showChat: false,
            userMessage: '',
            messages: [],
        };
    },
    mounted() {
        // Greet when opened for first time
        this.sendAutoMessage('Type "start" or "hi" to begin chatting!');
    },
    methods: {
        toggleChat() {
            this.showChat = !this.showChat;
            this.$nextTick(() => this.scrollToBottom());
        },

        sendAutoMessage(text) {
            this.messages.push({ sender: 'bot', text });
        },

        async sendMessage() {
            if (!this.userMessage.trim()) return;

            const message = this.userMessage.trim();
            this.messages.push({ sender: 'user', text: message });
            this.userMessage = '';
            this.scrollToBottom();

            try {
                let res;
                try {
                    res = await axios.post('/api/chatbot', { message }, {
                        headers: { 'Content-Type': 'application/json' },
                    });
                } catch (error) {
                    if (error.response && error.response.status === 405) {
                        res = await axios.get('/api/chatbot', {
                            params: { message },
                        });
                    } else {
                        throw error;
                    }
                }

                const data = res.data;

                // Bot reply
                this.messages.push({ sender: 'bot', text: data.reply });
                this.scrollToBottom();

                // If there are options, show them
                if (data.options) {
                    data.options.forEach((opt) => {
                        if (typeof opt === 'string') {
                            this.messages.push({ sender: 'bot-option', text: opt, value: opt });
                        } else {
                            this.messages.push({
                                sender: 'bot-option',
                                text: opt.label || opt.value || '',
                                value: opt.value || opt.label || '',
                                url: opt.url || null,
                                openInNewTab: Boolean(opt.open_in_new_tab),
                            });
                        }
                    });
                    this.scrollToBottom();
                }
            } catch (error) {
                this.messages.push({ sender: 'bot', text: '⚠️ Error connecting to server.' });
            }
        },

        handleOptionClick(msg) {
            if (msg.sender === 'bot-option') {
                if (msg.url) {
                    const targetUrl = this.resolveOptionUrl(msg.url);
                    window.open(targetUrl, msg.openInNewTab ? '_blank' : '_self');
                    return;
                }

                const choice = (msg.value || msg.text || '').toString().trim();
                if (!choice) return;

                this.userMessage = choice;
                this.sendMessage();
            }
        },

        resolveOptionUrl(url) {
            if (!url) return '';
            return url.startsWith('http') ? url : `${window.location.origin}${url}`;
        },

        scrollToBottom() {
            this.$nextTick(() => {
                const chatBody = this.$refs.chatBody;
                if (chatBody) chatBody.scrollTop = chatBody.scrollHeight;
            });
        },
    },
};
</script>

<style scoped>
@keyframes shake {
    0% { transform: translateX(0); }
    20% { transform: translateX(-3px); }
    40% { transform: translateX(3px); }
    60% { transform: translateX(-3px); }
    80% { transform: translateX(3px); }
    100% { transform: translateX(0); }
}

.chat-button {
    position: fixed;
    bottom: 20px;
    right: 20px;
    background: #fff;
    color: #fff;
    border-radius: 50%;
    width: 60px;
    height: 60px;
    font-size: 26px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
    z-index: 1000;

    /* 👇 animation */
    animation: shake 1.5s ease-in-out infinite;
}

.chat-window {
    position: fixed;
    bottom: 90px;
    right: 20px;
    width: 320px;
    height: 420px;
    background: #fff;
    border-radius: 10px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    z-index: 1001;
}

.chat-header {
    background: #007bff;
    color: #fff;
    padding: 10px;
    font-weight: bold;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.close-btn {
    background: transparent;
    border: none;
    color: white;
    font-size: 16px;
    cursor: pointer;
}

.chat-body {
    flex: 1;
    padding: 10px;
    overflow-y: auto;
    background: #f9f9f9;
}

.message {
    margin-bottom: 8px;
    padding: 8px 10px;
    border-radius: 10px;
    max-width: 80%;
    word-wrap: break-word;
}

.message.user {
    background: #d1e7ff;
    align-self: flex-end;
    margin-left: auto;
}

.message.bot {
    background: #e8f0fe;
    align-self: flex-start;
}

.message.bot-option {
    background: #fff;
    border: 1px solid #007bff;
    color: #007bff;
    align-self: flex-start;
    cursor: pointer;
    transition: 0.2s;
}

.message.bot-option:hover {
    background: #007bff;
    color: #fff;
}

.message.bot-option a {
    color: inherit;
    text-decoration: none;
    display: block;
}

.chat-input {
    display: flex;
    border-top: 1px solid #eee;
    padding: 8px;
}

.chat-input input {
    flex: 1;
    border: 1px solid #ccc;
    border-radius: 20px;
    padding: 6px 12px;
    outline: none;
}

.chat-input button {
    background: #007bff;
    color: #fff;
    border: none;
    border-radius: 20px;
    padding: 6px 14px;
    margin-left: 6px;
    cursor: pointer;
}

.slide-up-enter-active,
.slide-up-leave-active {
    transition: all 0.3s ease;
}

.slide-up-enter,
.slide-up-leave-to {
    opacity: 0;
    transform: translateY(20px);
}
</style>
